import { Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import {
  CreateManualExpeditionInput,
  ReceptionDto,
  ReceptionReferenceOption,
} from '@anessa/shared-models';
import { WeighbridgeEventsApiBase } from '../../procurement/weighbridge-events-api.base';
import { ReferenceOptions } from '../../procurement/exchange/weighbridge-exchange-api.service';

@Injectable({ providedIn: 'root' })
export class ExpeditionsApiService extends WeighbridgeEventsApiBase {
  protected readonly resource = 'expeditions';

  /** Persist the editable departure stock ("Stock origine"). Expeditions only. */
  setStockOrigin(plantId: string, id: string, stockOrigin: string): Observable<ReceptionDto> {
    return this.http.patch<ReceptionDto>(
      `/api/${this.resource}/${id}/stock-origin?plantId=${encodeURIComponent(plantId)}`,
      { stockOrigin },
    );
  }

  /** Persist the editable valorisation. Expeditions only. */
  setValorisation(plantId: string, id: string, valorisation: string): Observable<ReceptionDto> {
    return this.http.patch<ReceptionDto>(
      `/api/${this.resource}/${id}/valorisation?plantId=${encodeURIComponent(plantId)}`,
      { valorisation },
    );
  }

  /** Persist the editable spreader. Expeditions only. */
  setSpreaderCode(
    plantId: string,
    id: string,
    spreaderCode: string,
    spreaderName: string,
  ): Observable<ReceptionDto> {
    return this.http.patch<ReceptionDto>(
      `/api/${this.resource}/${id}/spreader?plantId=${encodeURIComponent(plantId)}`,
      { spreaderCode, spreaderName },
    );
  }

  /**
   * The Épandeur (spreader) choices: the plant's active relevant Regener transporters, as
   * Données Site › Transporteurs lists them with Source = Regener and "Pertinents
   * uniquement" (ADEV-1226). A record's stored spreader may lie outside this list.
   */
  getSpreaderOptions(plantId: string): Observable<ReceptionReferenceOption[]> {
    return this.http.get<ReceptionReferenceOption[]>(`/api/${this.resource}/spreader-options`, {
      params: { plantId },
    });
  }

  /**
   * The plant's Precia reference options for the manual-creation dialog — the
   * same plant-scoped set the weighbridge receives, so a hand-logged expedition
   * can only reference codes the scale itself would have been given.
   */
  getReferenceOptions(plantId: string): Observable<ReferenceOptions> {
    return this.http.get<ReferenceOptions>(
      `/api/${this.resource}/reference-options`,
      { params: { plantId } },
    );
  }

  /** Manually create an expedition record (no scale/import data behind it). */
  create(plantId: string, input: CreateManualExpeditionInput): Observable<ReceptionDto> {
    return this.http.post<ReceptionDto>(
      `/api/${this.resource}?plantId=${encodeURIComponent(plantId)}`,
      input,
    );
  }

  /**
   * Download the accompanying document (DAC or BSD) for an expedition as a PDF.
   * Returns the full response so the caller can honour the server's filename
   * (Content-Disposition), which follows the uploaded-attachment naming scheme.
   */
  getDocument(
    plantId: string,
    id: string,
    type: 'DAC' | 'BSD',
  ): Observable<HttpResponse<Blob>> {
    const params = { plantId, type };
    return this.http.get(`/api/${this.resource}/${id}/document`, {
      params,
      responseType: 'blob',
      observe: 'response',
    });
  }

  /** Count of expeditions still missing a stock origin (sidebar dot). */
  getMissingOriginCount(plantId: string): Observable<number> {
    return this.http
      .get<{ count: number }>(`/api/${this.resource}/missing-origin-count`, {
        params: { plantId },
      })
      .pipe(map((r) => r.count));
  }
}
